import React, { useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@roger/ui-components';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { setPortTypeFaxNumberPortsPortOrderIdPortTypePut as setPortType } from '../../../services/api/sdk.gen';
import type {
  PortOrderDetail,
  PortTypeRequest,
} from '../../../services/api/types.gen';
import {
  describeError,
  displayPhone,
  logFailure,
  type Notify,
  toE164,
} from './shared';

type Choice = 'full' | 'partial';

const NoLongerFullAlert: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Alert className='border-amber-300 bg-amber-50 text-amber-800'>
    <AlertTriangle className='h-4 w-4' />
    <AlertTitle>This port is no longer a full port</AlertTitle>
    <AlertDescription>{children}</AlertDescription>
  </Alert>
);

/**
 * Whether the rest of the carrier account stays. A full port closes the
 * account, so it always takes the account's main number typed to confirm. An
 * invoice listing only the numbers being moved only suggests it: the radio
 * shows the saved type until the person chooses full and confirms.
 */
const PortTypeSection: React.FC<{
  detail: PortOrderDetail;
  editable: boolean;
  onChanged: (detail: PortOrderDetail) => void;
  showError: Notify;
}> = ({ detail, editable, onChanged, showError }) => {
  const offer = detail.invoice_port_offer;
  const [choosingFull, setChoosingFull] = useState(false);
  // The offer last seen, so a new invoice drops an unconfirmed full choice.
  const [seenOffer, setSeenOffer] = useState(offer);
  const [mainNumber, setMainNumber] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // The port type last seen, so a full port turned partial elsewhere is said
  // out loud. A choice made here updates it first, so it never notices.
  const [seenType, setSeenType] = useState(detail.port_type);
  const [noLongerFull, setNoLongerFull] = useState(false);

  const isFull = detail.port_type === 'full';
  if (offer !== seenOffer) {
    setSeenOffer(offer);
    setChoosingFull(false);
  }
  if (detail.port_type !== seenType) {
    setSeenType(detail.port_type);
    setNoLongerFull(seenType === 'full');
  }

  const save = async (body: PortTypeRequest) => {
    setIsSaving(true);
    try {
      const { data } = await setPortType({
        path: { port_order_id: detail.id },
        body,
        throwOnError: true,
      });
      setChoosingFull(false);
      setMainNumber('');
      setSeenType(data.port_type);
      setNoLongerFull(false);
      onChanged(data);
    } catch (error) {
      logFailure('Failed to set the port type', error);
      showError(describeError(error, 'Unable to change the port type.'));
    } finally {
      setIsSaving(false);
    }
  };

  const choose = (choice: Choice) => {
    if (choice === 'partial') {
      setChoosingFull(false);
      if (isFull) void save({ type: 'partial' });
      return;
    }
    if (!isFull) setChoosingFull(true);
  };

  if (offer === 'partial' && !isFull) {
    return (
      <section className='space-y-2'>
        <h4 className='text-sm font-semibold'>
          What happens to your carrier account
        </h4>
        {noLongerFull ? (
          <NoLongerFullAlert>
            Your invoice lists other numbers on this account, so some numbers
            stay with your carrier.
          </NoLongerFullAlert>
        ) : (
          <p className='text-sm text-muted-foreground'>
            Your invoice lists other numbers, so some numbers stay with your
            current carrier.
          </p>
        )}
      </section>
    );
  }

  const selected: Choice =
    isFull || (editable && choosingFull) ? 'full' : 'partial';
  const typed = toE164(mainNumber);
  const invoiceNumbers = detail.invoice_account_phone_numbers
    .map(displayPhone)
    .join(', ');

  const options: { value: Choice; label: string; hint?: string }[] =
    offer === 'full'
      ? [
          {
            value: 'full',
            label: 'Every number on this account is moving',
            hint: `Your invoice lists only these numbers: ${invoiceNumbers}. Check these are all the numbers on the account.`,
          },
          {
            value: 'partial',
            label: 'Some numbers stay with your current carrier',
            hint: 'You keep the account and the numbers you are not moving.',
          },
        ]
      : [
          {
            value: 'partial',
            label: 'Some numbers stay with your current carrier',
          },
          { value: 'full', label: 'Every number on my account is moving' },
        ];

  return (
    <section className='space-y-3'>
      <h4 className='text-sm font-semibold'>
        What happens to your carrier account
      </h4>
      <RadioGroup
        value={selected}
        onValueChange={value => choose(value as Choice)}
        disabled={!editable || isSaving}
        className='gap-3'
        aria-label='What happens to your carrier account'
      >
        {options.map(option => (
          <div key={option.value} className='flex items-start gap-2'>
            <RadioGroupItem
              value={option.value}
              id={`port-type-${option.value}`}
              className='mt-0.5'
            />
            <div className='grid gap-0.5'>
              <Label htmlFor={`port-type-${option.value}`}>
                {option.label}
              </Label>
              {option.hint && (
                <span className='text-xs text-muted-foreground'>
                  {option.hint}
                </span>
              )}
            </div>
          </div>
        ))}
      </RadioGroup>

      {noLongerFull && !isFull && (
        <NoLongerFullAlert>
          To move every number, choose it again and type your account&apos;s
          main number.
        </NoLongerFullAlert>
      )}

      {editable && choosingFull && !isFull && (
        <div className='grid gap-1.5'>
          <Label htmlFor='port-confirm-main'>
            Type your account&apos;s main number to confirm
          </Label>
          <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
            <Input
              id='port-confirm-main'
              type='tel'
              autoComplete='off'
              className='w-full sm:w-56'
              value={mainNumber}
              disabled={isSaving}
              onChange={event => setMainNumber(event.target.value)}
            />
            <Button
              size='sm'
              disabled={!editable || !typed || isSaving}
              onClick={() => {
                if (typed)
                  void save({ type: 'full', confirm_main_number: typed });
              }}
            >
              {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Confirm
            </Button>
          </div>
          <span className='text-xs font-medium text-amber-800'>
            Not saved until you confirm
          </span>
          <span className='text-xs text-muted-foreground'>
            Type the main number on your carrier account, as your carrier shows
            it. Your carrier account closes when the port completes.
          </span>
        </div>
      )}

      {isFull && (
        <Alert className='border-amber-300 bg-amber-50 text-amber-800'>
          <AlertTriangle className='h-4 w-4' />
          <AlertTitle>
            Your carrier account closes when the port completes
          </AlertTitle>
          <AlertDescription>
            Any other service on it, such as a voice line or internet, stops
            too.
          </AlertDescription>
        </Alert>
      )}
    </section>
  );
};

export default PortTypeSection;
